/**
 * Command Palette (Cmd+K) — global search and navigation overlay.
 *
 * Sections:
 *   - Search results (companies by name/domain/ticker)
 *   - Recent companies (from localStorage, shown when search is empty)
 *   - Quick actions (Add Company, etc.)
 *
 * Keyboard: Arrow keys navigate, Enter selects, Escape closes.
 * Deep links: "McKesson > News" navigates to company's News tab.
 *
 * Covers: AC-014 through AC-021
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, Plus, Clock, ArrowRight } from 'lucide-react';
import { useCompanies } from '@/hooks/useCompanies';
import { useRecentCompanies } from '@/hooks/useRecentCompanies';
import type { CompanyListItem } from '@/types';

/** Valid tab names on the company detail page */
const VALID_TABS = ['overview', 'news', 'products', 'customers', 'jobs', 'leadership'] as const;

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PaletteItem {
  id: string;
  type: 'company' | 'recent' | 'action';
  label: string;
  sublabel?: string;
  ticker?: string;
  companyType?: string;
  companyId?: string;
  href?: string;
  action?: () => void;
}

const companyTypeLabels: Record<string, string> = {
  public: 'Public',
  pe_backed: 'PE-Backed',
  private: 'Private',
  subsidiary: 'Subsidiary',
};

/**
 * Parse a deep link query: "CompanyName > TabName"
 * Returns the company search part and the target tab (if valid).
 */
function parseDeepLink(query: string): { search: string; tab: string | null } {
  const parts = query.split('>').map((s) => s.trim());
  if (parts.length >= 2) {
    const search = parts[0]!;
    const tabCandidate = parts[1]!.toLowerCase();
    const tab = VALID_TABS.includes(tabCandidate as (typeof VALID_TABS)[number])
      ? tabCandidate
      : null;
    return { search, tab };
  }
  return { search: query, tab: null };
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { data: companies = [], isLoading } = useCompanies();
  const { recentCompanyIds } = useRecentCompanies();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset state when palette opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      // Focus input after render
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  // Build items list
  const items = useMemo((): PaletteItem[] => {
    const result: PaletteItem[] = [];

    if (query.trim()) {
      // Search mode: filter companies
      const { search } = parseDeepLink(query);
      const searchLower = search.toLowerCase();

      const matched = companies.filter((c) => {
        const name = c.name.toLowerCase();
        const domain = c.domain.toLowerCase();
        const ticker = (c.ticker || '').toLowerCase();
        return (
          name.includes(searchLower) || domain.includes(searchLower) || ticker.includes(searchLower)
        );
      });

      for (const company of matched) {
        result.push({
          id: `company-${company.id}`,
          type: 'company',
          label: company.name,
          sublabel: company.domain,
          ticker: company.ticker,
          companyType: companyTypeLabels[company.company_type] || company.company_type,
          companyId: company.id,
        });
      }
    } else {
      // Empty search: show recent companies + actions
      if (recentCompanyIds.length > 0) {
        const recentCompanies = recentCompanyIds
          .map((id) => companies.find((c) => c.id === id))
          .filter((c): c is CompanyListItem => c !== undefined);

        for (const company of recentCompanies) {
          result.push({
            id: `recent-${company.id}`,
            type: 'recent',
            label: company.name,
            sublabel: company.domain,
            ticker: company.ticker,
            companyType: companyTypeLabels[company.company_type] || company.company_type,
            companyId: company.id,
          });
        }
      }

      // Quick actions
      result.push({
        id: 'action-add',
        type: 'action',
        label: 'Add Company',
        sublabel: 'Track a new company',
      });
      result.push({
        id: 'action-companies',
        type: 'action',
        label: 'View All Companies',
        sublabel: 'Go to company list',
        href: '/companies',
      });
    }

    return result;
  }, [query, companies, recentCompanyIds]);

  // Keep selectedIndex in bounds
  useEffect(() => {
    if (selectedIndex >= items.length) {
      setSelectedIndex(Math.max(0, items.length - 1));
    }
  }, [items.length, selectedIndex]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedEl = listRef.current?.querySelector('[data-selected="true"]');
    selectedEl?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (item: PaletteItem) => {
      if (item.companyId) {
        const { tab } = parseDeepLink(query);
        if (tab) {
          navigate(`/companies/${item.companyId}?tab=${tab}`);
        } else {
          navigate(`/companies/${item.companyId}`);
        }
        onClose();
      } else if (item.href) {
        navigate(item.href);
        onClose();
      } else if (item.action) {
        item.action();
        onClose();
      }
    },
    [navigate, onClose, query]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, items.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (items[selectedIndex]) {
            handleSelect(items[selectedIndex]!);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [items, selectedIndex, handleSelect, onClose]
  );

  if (!isOpen) return null;

  // Group items by type for section headers
  const hasCompanies = items.some((i) => i.type === 'company');
  const hasRecent = items.some((i) => i.type === 'recent');
  const hasActions = items.some((i) => i.type === 'action');
  const hasResults = items.length > 0;
  const isSearching = query.trim().length > 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50"
        onClick={onClose}
        data-testid="command-palette-backdrop"
      />

      {/* Palette */}
      <div className="fixed inset-x-0 top-[20%] z-50 mx-auto max-w-lg">
        <div className="rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
          {/* Search input */}
          <div className="flex items-center border-b border-gray-200 px-4">
            <Search className="h-5 w-5 text-gray-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search companies, navigate, or type a command..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              onKeyDown={handleKeyDown}
              className="flex-1 h-12 px-3 text-sm bg-transparent outline-none placeholder:text-gray-400"
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-72 overflow-y-auto py-2">
            {isLoading && (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                Loading companies...
              </div>
            )}

            {!isLoading && !hasResults && isSearching && (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                No results found for &ldquo;{query}&rdquo;
              </div>
            )}

            {/* Companies section */}
            {hasCompanies && (
              <>
                <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Companies
                </div>
                {items
                  .filter((i) => i.type === 'company')
                  .map((item, _idx) => {
                    const globalIdx = items.indexOf(item);
                    return (
                      <PaletteRow
                        key={item.id}
                        item={item}
                        isSelected={globalIdx === selectedIndex}
                        onSelect={() => handleSelect(item)}
                        onHover={() => setSelectedIndex(globalIdx)}
                      />
                    );
                  })}
              </>
            )}

            {/* Recent section */}
            {hasRecent && (
              <>
                <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Recent
                </div>
                {items
                  .filter((i) => i.type === 'recent')
                  .map((item) => {
                    const globalIdx = items.indexOf(item);
                    return (
                      <PaletteRow
                        key={item.id}
                        item={item}
                        isSelected={globalIdx === selectedIndex}
                        onSelect={() => handleSelect(item)}
                        onHover={() => setSelectedIndex(globalIdx)}
                      />
                    );
                  })}
              </>
            )}

            {/* Actions section */}
            {hasActions && (
              <>
                <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Actions
                </div>
                {items
                  .filter((i) => i.type === 'action')
                  .map((item) => {
                    const globalIdx = items.indexOf(item);
                    return (
                      <PaletteRow
                        key={item.id}
                        item={item}
                        isSelected={globalIdx === selectedIndex}
                        onSelect={() => handleSelect(item)}
                        onHover={() => setSelectedIndex(globalIdx)}
                      />
                    );
                  })}
              </>
            )}
          </div>

          {/* Footer with keyboard hints */}
          <div className="border-t border-gray-200 px-4 py-2 flex items-center gap-4 text-[10px] text-gray-400">
            <span>
              <kbd className="rounded border border-gray-200 px-1 py-0.5 font-mono">&uarr;</kbd>
              <kbd className="rounded border border-gray-200 px-1 py-0.5 font-mono ml-0.5">
                &darr;
              </kbd>{' '}
              navigate
            </span>
            <span>
              <kbd className="rounded border border-gray-200 px-1 py-0.5 font-mono">Enter</kbd>{' '}
              select
            </span>
            <span>
              <kbd className="rounded border border-gray-200 px-1 py-0.5 font-mono">ESC</kbd> close
            </span>
            <span className="ml-auto">
              Type <strong>company &gt; tab</strong> for deep links
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

/** Single row in the palette results list */
function PaletteRow({
  item,
  isSelected,
  onSelect,
  onHover,
}: {
  item: PaletteItem;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
}) {
  const icon =
    item.type === 'action' ? (
      item.id === 'action-add' ? (
        <Plus className="h-4 w-4 text-gray-400" />
      ) : (
        <ArrowRight className="h-4 w-4 text-gray-400" />
      )
    ) : item.type === 'recent' ? (
      <Clock className="h-4 w-4 text-gray-400" />
    ) : (
      <Building2 className="h-4 w-4 text-gray-400" />
    );

  return (
    <button
      className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${
        isSelected ? 'bg-primary-50 text-primary-900' : 'text-gray-700 hover:bg-gray-50'
      }`}
      data-selected={isSelected}
      onClick={onSelect}
      onMouseEnter={onHover}
    >
      {icon}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{item.label}</span>
          {item.ticker && (
            <span className="shrink-0 inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
              {item.ticker}
            </span>
          )}
          {item.companyType && (
            <span className="shrink-0 inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
              {item.companyType}
            </span>
          )}
        </div>
        {item.sublabel && <div className="text-xs text-gray-500 truncate">{item.sublabel}</div>}
      </div>
    </button>
  );
}

export default CommandPalette;
